import { apiClient } from './client';

export interface weeklyreportDomainRow {
  domain: string;
  count: number;
}

export interface weeklyreportHourRow {
  hour: number;
  count: number;
}

export interface weeklyReportChannelTotals {
  website: number;
  email: number;
}

export interface weeklyReportCost {
  totalUsd: number;
  totalSek: number;
  perChatSek: number;
  usdSekRate: number;
}

export interface weeklyReportTokens {
  total: number;
  prompt: number;
  completion: number;
  cached: number;
}

export interface weeklyReportTopRoute {
  route: string;
  runCount: number;
  failCount: number;
}

export interface weeklyReportKnownIssue {
  id: string;
  type: string;
  severity: string;
  status: string;
  userMessage: string;
  createdAt: string;
  conversationId: string;
}

export interface weeklyReportTotals {
  total: number;
  byChannel: weeklyReportChannelTotals;
  byDomain: weeklyreportDomainRow[];
  withoutDomain: number;
  byHour: weeklyreportHourRow[];
  handoff: { total: number; byChannel: weeklyReportChannelTotals };
  emailDraft: { total: number; sent: number; failed: number; pending: number };
  cost: weeklyReportCost;
  tokens: weeklyReportTokens;
}

export interface weeklyReportChange {
  total: number;
  percent: number | null;
}

export interface weeklyReportResponse {
  week: {
    isoWeek: number;
    isoYear: number;
    label: string;
    from: string;
    to: string;
    isCurrent: boolean;
  };
  totals: weeklyReportTotals;
  totalsExcludingGreetings: weeklyReportTotals;
  greetings: { count: number };
  previousWeek: {
    isoWeek: number;
    isoYear: number;
    total: number;
    totalExcludingGreetings: number;
    byChannel: weeklyReportChannelTotals;
  };
  change: weeklyReportChange;
  changeExcludingGreetings: weeklyReportChange;
  knownIssues: weeklyReportKnownIssue[];
  notes: string;
  issuesNotes: string;
  topRoutes: weeklyReportTopRoute[];
  topRoutesExcludingGreetings: weeklyReportTopRoute[];
  currentWeek: { isoWeek: number; isoYear: number };
}

export type WeekSelection = {
  isoYear: number;
  isoWeek: number;
} | null;

export const getWeeklyreport = async (selection: WeekSelection): Promise<weeklyReportResponse> => {
  const query = selection ? `?year=${selection.isoYear}&week=${selection.isoWeek}` : '';
  const { data } = await apiClient.get<weeklyReportResponse>(`/weekly-report${query}`);
  return data;
};

export const saveWeeklyNotes = async (input: {
  year: number;
  week: number;
  notes?: string;
  issuesNotes?: string;
}): Promise<void> => {
  await apiClient.put('/weekly-report/notes', input);
};

export interface weeklyReportSendFailure {
  to: string;
  error: string;
}

export type SendResult =
  | { ok: true; recipients: string[]; failed?: weeklyReportSendFailure[]; sentAt: string }
  | { alreadySent: true; sentAt: string };

export const sendWeeklyReport = async (input: {
  year: number;
  week: number;
  force?: boolean;
  recipients?: string[];
  excludeGreetings?: boolean;
}): Promise<SendResult> => {
  try {
    const { data } = await apiClient.post('/weekly-report/send', input);
    return data as SendResult;
  } catch (err: any) {
    if (err?.response?.status === 409) {
      return { alreadySent: true, sentAt: err.response.data?.sentAt };
    }
    throw err;
  }
};

export const setFeedbackStatus = async (input: {
  id: string;
  status: 'resolved' | 'new';
}): Promise<void> => {
  await apiClient.patch(`/admin/feedback/${input.id}/status`, { status: input.status });
};
